import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import React, { useCallback, useId, useRef, useState } from 'react';
import { useOnClickOutside } from '../hooks';
import styles from './Modal.module.css';
import { SearchField } from './SearchField';
import { SelectOption } from './Select';
import { Spinner } from './Spinner';

export interface SearchFieldWithOptionsProps<T> {
  hasMore?: boolean;
  fetchNextPage: () => Promise<unknown>;
  options: SelectOption<T>[];
  classes?: {
    container?: string;
    popup?: string;
    field?: string;
  };
  onSelect: (item: T) => void;
  onSearch: (search: string) => void;
  closeOnSelect?: boolean;
  clearOnSelect?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  icon?: string;
  renderLabel?: (option: SelectOption<T>) => React.ReactNode;
}

export const SearchFieldWithOptions = <T extends string | number | Record<string, unknown>>({
  icon,
  hasMore,
  options,
  classes,
  onSelect,
  onSearch,
  placeholder,
  renderLabel,
  fetchNextPage,
  clearOnSelect,
  closeOnSelect,
  isLoading = false,
}: SearchFieldWithOptionsProps<T>) => {
  const [open, setOpen] = useState(false);
  const scrollableId = useId();
  const listBlockRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<{ onClear: () => void; input: HTMLInputElement | null } | null>(null);

  useOnClickOutside({
    ref: listBlockRef,
    handler: () => setOpen(false),
  });

  const handleSelect = useCallback(
    (value: T) => {
      onSelect(value);
      closeOnSelect && setOpen(false);
      if (clearOnSelect && inputRef.current) {
        inputRef.current.onClear();
      }
    },
    [clearOnSelect, closeOnSelect, onSelect, inputRef],
  );

  return (
    <div
      className={classNames(
        'w-full rounded-lg flex flex-col bg-white z-20',
        { 'max-h-[500px]': !classes?.container?.includes('max-h-') },
        classes?.container,
      )}
      ref={listBlockRef}
    >
      <div className="relative">
        <SearchField
          ref={inputRef}
          isLoading={isLoading}
          placeholder={placeholder}
          onChange={onSearch}
          className={classes?.field}
          icon={icon}
          inputPops={{ onClick: () => setOpen(prev => !prev) }}
        />
        {open && (
          <div
            id={scrollableId}
            className={classNames(
              'absolute w-full mt-2 max-h-48 overflow-y-auto rounded-md bg-white z-20 border',
              classes?.popup,
            )}
          >
            {isLoading && (
              <div className="w-full h-40 flex justify-center items-center">
                <Spinner />
              </div>
            )}
            {!isLoading && !options.length && (
              <div className="w-full h-40 flex justify-center items-center">
                <span className="font-semibold">No items found</span>
              </div>
            )}
            <InfiniteScroll
              next={fetchNextPage}
              hasMore={!!hasMore}
              loader={
                <div className="flex justify-center items-center h-16 w-full">
                  <Spinner size="md" variant="primary" />
                </div>
              }
              scrollableTarget={scrollableId}
              dataLength={options.length}
              className={classNames('w-full flex flex-col gap-2', styles.modalScroll)}
            >
              {options.map((option, index) => {
                const { label, value } = option;
                return (
                  <div
                    key={`${index}_${value}`}
                    onClick={handleSelect.bind(null, value)}
                    className="flex gap-3 items-center justify-start hover:cursor-pointer hover:bg-gray-100 px-5 py-2"
                  >
                    {renderLabel ? (
                      renderLabel(option)
                    ) : (
                      <span className="text-sm leading-[22px]">{label}</span>
                    )}
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
        )}
      </div>
    </div>
  );
};
